import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NdfOperationFilterRequest } from '@app/core/models/operation/ndf-operation-filter-request';
import { VaultTranslatedBaseComponent } from '@app/shared/components/base/vault-translated-base.component';
import { ApiResponse } from '@interfaces/common/apiResponse';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { FinancialInstitutionService } from '@services/http/financial-institution.service';
import { FinancialInstitution } from '@src/app/core/interfaces/a-organizar/financialInstitution';
import { CognitoRxjsOperator } from '@src/app/core/rxjs-operators/cognito-rxjs-operator';
import { ActiveFilterConfig } from '@src/app/shared/interfaces/active-filter.interface';
import { Observable, map } from 'rxjs';

@Component({
  selector: 'app-ndf-operation-filter',
  templateUrl: './ndf-operation-filter.component.html',
  styleUrls: ['./ndf-operation-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NdfOperationFilterComponent
  extends VaultTranslatedBaseComponent
  implements OnInit, OnChanges
{
  @Input() isActive: boolean = false;
  @Input() currencyOptions: Option[] = [];
  @Input() operationMaxValue: number = 100000;
  @Input() currentFilters: NdfOperationFilterRequest | null = null;

  @Output() sendCloseFilter = new EventEmitter<boolean>();
  @Output() sendFilter = new EventEmitter<NdfOperationFilterRequest>();
  @Output() sendActiveFilters = new EventEmitter<ActiveFilterConfig[]>();

  filterForm!: FormGroup;

  // Opções estáticas
  operationStatusOptions: Option[] = [
    { label: this.t(this.tk.Management.NdfSimulated), value: 'Simulated' },
    { label: this.t(this.tk.Management.NdfQuoted), value: 'Quoted' },
    { label: this.t(this.tk.Management.NdfHired), value: 'Hired' },
  ];

  financialStatusOptions: Option[] = [
    { label: this.t(this.tk.Management.NdfOpen), value: 'Open' },
    { label: this.t(this.tk.Management.NdfSettled), value: 'Settled' },
    { label: this.t(this.tk.Management.NdfSettledReversed), value: 'Reversed' },
  ];

  compositionOptions: Option[] = [
    { label: this.t(this.tk.Management.NdfFull), value: 'Full' },
    { label: this.t(this.tk.Management.NdfPartial), value: 'Partial' },
  ];

  installmentsOptions: Option[] = [
    { label: 'Todas', value: '' },
    { label: 'Com Parcelas', value: 'false' },
    { label: 'Sem Parcelas', value: 'true' },
  ];

  operationStrategyTypeOptions: Option[] = [
    { label: 'CallSpread', value: 'Callspread' },
    { label: 'NDF Com CAP', value: 'NDFCAP' },
    { label: 'Swap', value: 'SWAP' },
    { label: 'SWAP com Cap', value: 'SwapWithCap' },
    { label: 'NDF Vanilla', value: 'NDFVanilla' },
    { label: 'Call Vanilla', value: 'CallVanilla' },
  ];

  // Observables para dados dinâmicos
  financialInstitutions$!: Observable<Option[]>;

  // Cache para resolução síncrona
  private currenciesCache: Option[] = [];
  private financialInstitutionsCache: Option[] = [];

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly formBuilder: FormBuilder,
    private readonly cognitoRxjsOperator: CognitoRxjsOperator,
    private readonly financialInstitutionService: FinancialInstitutionService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    translateService: TranslateService
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadFinancialInstitutions();
    this.loadFiltersFromUrl();
    this.populateFormWithCurrentFilters();

    // Escutar mudanças na URL
    this.route.queryParams.subscribe(() => {
      this.loadFiltersFromUrl();
      this.populateFormWithCurrentFilters();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currencyOptions']) {
      this.currenciesCache = this.currencyOptions;
      this.emitActiveFilters();
    }

    if (changes['operationMaxValue']) {
      this.updateMaxValues();
    }

    if (changes['currentFilters']) {
      if (this.filterForm) {
        this.populateFormWithCurrentFilters();
      }
      this.emitActiveFilters();
    }
  }

  private initializeForm(): void {
    this.filterForm = this.formBuilder.group({
      internationalCurrencyId: [''],
      financialInstitutionId: [''],
      financialInstitutionName: [''],
      operationStatus: [''],
      financialStatus: [''],
      composition: [''],
      withoutInstallments: [''],
      operationStrategyTypes: [[]],
      valueMEFrom: [''],
      valueMETo: [''],
      daysCountFrom: [''],
      daysCountTo: [''],
      notionalAmountFrom: [''],
      notionalAmountTo: [''],
      forwardRateFrom: [''],
      forwardRateTo: [''],
      capValueFrom: [''],
      capValueTo: [''],
      referenceExchangeRateFrom: [''],
      referenceExchangeRateTo: [''],
      dueAtFrom: [''],
      dueAtTo: [''],
      operationDateFrom: [''],
      operationDateTo: [''],
      remarks: [''],
    });
  }

  private updateMaxValues(): void {
    // Não é mais necessário atualizar valores máximos para inputs
  }

  private loadFinancialInstitutions(): void {
    this.financialInstitutions$ = this.financialInstitutionService
      .getFinancialInstitutions({ PageSize: 500 })
      .pipe(
        this.cognitoRxjsOperator.httpHandler(this.destroyRef, {
          showLoading: false,
        }),
        map((response: ApiResponse<FinancialInstitution[]> | null) =>
          (response?.data || []).map((institution: FinancialInstitution) => ({
            label: `${institution.displayName} | ${institution.compensationCode}`,
            value: institution.id,
            data: institution,
          }))
        )
      );

    this.financialInstitutions$.subscribe(options => {
      this.financialInstitutionsCache = options;
      this.emitActiveFilters();
    });
  }

  private loadFiltersFromUrl(): void {
    const queryParams = this.route.snapshot.queryParams;

    if (Object.keys(queryParams).length > 0) {
      // Converter query params para filtros
      const filters: NdfOperationFilterRequest = {};

      Object.keys(queryParams).forEach(key => {
        const value = queryParams[key];
        if (value !== undefined && value !== null && value !== '') {
          // Converter arrays de volta para arrays se necessário
          if (
            (key.includes('Ids') || key === 'operationStrategyTypes') &&
            typeof value === 'string'
          ) {
            (filters as Record<string, unknown>)[key] = value.split(',');
          } else if (key === 'withoutInstallments') {
            // Converter string para boolean
            (filters as Record<string, unknown>)[key] =
              value === 'true' ? true : value === 'false' ? false : undefined;
          } else {
            (filters as Record<string, unknown>)[key] = value;
          }
        }
      });

      this.currentFilters = Object.keys(filters).length > 0 ? filters : null;
    }
  }

  private updateUrlWithFilters(filters: NdfOperationFilterRequest): void {
    const queryParams: Record<string, string | number | boolean | string[]> = {};

    // Preservar query params não relacionados a filtros
    const currentParams = this.route.snapshot.queryParams;
    const filterKeys = Object.keys(filters);

    Object.keys(currentParams).forEach(key => {
      if (!filterKeys.includes(key)) {
        queryParams[key] = currentParams[key];
      }
    });

    // Adicionar novos filtros
    Object.keys(filters).forEach(key => {
      const value = filters[key as keyof NdfOperationFilterRequest];
      if (value !== undefined && value !== null && value !== '') {
        // Converter arrays para strings
        if (Array.isArray(value)) {
          queryParams[key] = value.join(',');
        } else {
          queryParams[key] = value;
        }
      }
    });

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  private populateFormWithCurrentFilters(): void {
    if (!this.filterForm) return;

    if (!this.currentFilters) {
      this.filterForm.reset();
      return;
    }

    this.filterForm.patchValue({
      internationalCurrencyId: this.currentFilters.internationalCurrencyId || '',
      financialInstitutionId: this.currentFilters.financialInstitutionId || '',
      financialInstitutionName: this.currentFilters.financialInstitutionName || '',
      operationStatus: this.currentFilters.operationStatus || '',
      financialStatus: this.currentFilters.financialStatus || '',
      composition: this.currentFilters.composition || '',
      withoutInstallments:
        this.currentFilters.withoutInstallments !== undefined
          ? String(this.currentFilters.withoutInstallments)
          : '',
      operationStrategyTypes: this.currentFilters.operationStrategyTypes || [],
      valueMEFrom: this.currentFilters.valueMEFrom || '',
      valueMETo: this.currentFilters.valueMETo || '',
      daysCountFrom: this.currentFilters.daysCountFrom || '',
      daysCountTo: this.currentFilters.daysCountTo || '',
      notionalAmountFrom: this.currentFilters.notionalAmountFrom || '',
      notionalAmountTo: this.currentFilters.notionalAmountTo || '',
      forwardRateFrom: this.currentFilters.forwardRateFrom || '',
      forwardRateTo: this.currentFilters.forwardRateTo || '',
      capValueFrom: this.currentFilters.capValueFrom || '',
      capValueTo: this.currentFilters.capValueTo || '',
      referenceExchangeRateFrom: this.currentFilters.referenceExchangeRateFrom || '',
      referenceExchangeRateTo: this.currentFilters.referenceExchangeRateTo || '',
      dueAtFrom: this.currentFilters.dueAtFrom || '',
      dueAtTo: this.currentFilters.dueAtTo || '',
      operationDateFrom: this.currentFilters.operationDateFrom || '',
      operationDateTo: this.currentFilters.operationDateTo || '',
      remarks: this.currentFilters.remarks || '',
    });

    this.emitActiveFilters();
  }

  clear(): void {
    this.filterForm.reset();
    this.submit();
  }

  submit(): void {
    const formValue = this.filterForm.value;

    // Validar ranges de valores
    if (formValue.valueMEFrom > formValue.valueMETo) {
      [formValue.valueMEFrom, formValue.valueMETo] = [formValue.valueMETo, formValue.valueMEFrom];
    }

    if (formValue.daysCountFrom > formValue.daysCountTo) {
      [formValue.daysCountFrom, formValue.daysCountTo] = [
        formValue.daysCountTo,
        formValue.daysCountFrom,
      ];
    }

    if (formValue.notionalAmountFrom > formValue.notionalAmountTo) {
      [formValue.notionalAmountFrom, formValue.notionalAmountTo] = [
        formValue.notionalAmountTo,
        formValue.notionalAmountFrom,
      ];
    }

    if (formValue.forwardRateFrom > formValue.forwardRateTo) {
      [formValue.forwardRateFrom, formValue.forwardRateTo] = [
        formValue.forwardRateTo,
        formValue.forwardRateFrom,
      ];
    }

    if (formValue.capValueFrom > formValue.capValueTo) {
      [formValue.capValueFrom, formValue.capValueTo] = [
        formValue.capValueTo,
        formValue.capValueFrom,
      ];
    }

    if (formValue.referenceExchangeRateFrom > formValue.referenceExchangeRateTo) {
      [formValue.referenceExchangeRateFrom, formValue.referenceExchangeRateTo] = [
        formValue.referenceExchangeRateTo,
        formValue.referenceExchangeRateFrom,
      ];
    }

    // Validar ranges de datas
    if (
      formValue.dueAtFrom &&
      formValue.dueAtTo &&
      new Date(formValue.dueAtFrom) > new Date(formValue.dueAtTo)
    ) {
      [formValue.dueAtFrom, formValue.dueAtTo] = [formValue.dueAtTo, formValue.dueAtFrom];
    }

    if (
      formValue.operationDateFrom &&
      formValue.operationDateTo &&
      new Date(formValue.operationDateFrom) > new Date(formValue.operationDateTo)
    ) {
      [formValue.operationDateFrom, formValue.operationDateTo] = [
        formValue.operationDateTo,
        formValue.operationDateFrom,
      ];
    }

    const filter: NdfOperationFilterRequest = {
      internationalCurrencyId: formValue.internationalCurrencyId,
      financialInstitutionId: formValue.financialInstitutionId,
      financialInstitutionName: formValue.financialInstitutionName,
      operationStatus: formValue.operationStatus,
      financialStatus: formValue.financialStatus,
      composition: formValue.composition,
      withoutInstallments:
        formValue.withoutInstallments === 'true'
          ? true
          : formValue.withoutInstallments === 'false'
            ? false
            : undefined,
      operationStrategyTypes: formValue.operationStrategyTypes || [],
      valueMEFrom: formValue.valueMEFrom ? formValue.valueMEFrom.toString() : '',
      valueMETo: formValue.valueMETo ? formValue.valueMETo.toString() : '',
      daysCountFrom: formValue.daysCountFrom ? formValue.daysCountFrom.toString() : '',
      daysCountTo: formValue.daysCountTo ? formValue.daysCountTo.toString() : '',
      notionalAmountFrom: formValue.notionalAmountFrom
        ? formValue.notionalAmountFrom.toString()
        : '',
      notionalAmountTo: formValue.notionalAmountTo ? formValue.notionalAmountTo.toString() : '',
      forwardRateFrom: formValue.forwardRateFrom ? formValue.forwardRateFrom.toString() : '',
      forwardRateTo: formValue.forwardRateTo ? formValue.forwardRateTo.toString() : '',
      capValueFrom: formValue.capValueFrom ? formValue.capValueFrom.toString() : '',
      capValueTo: formValue.capValueTo ? formValue.capValueTo.toString() : '',
      referenceExchangeRateFrom: formValue.referenceExchangeRateFrom
        ? formValue.referenceExchangeRateFrom.toString()
        : '',
      referenceExchangeRateTo: formValue.referenceExchangeRateTo
        ? formValue.referenceExchangeRateTo.toString()
        : '',
      dueAtFrom: formValue.dueAtFrom ? formValue.dueAtFrom.split('T')[0] : '',
      dueAtTo: formValue.dueAtTo ? formValue.dueAtTo.split('T')[0] : '',
      operationDateFrom: formValue.operationDateFrom
        ? formValue.operationDateFrom.split('T')[0]
        : '',
      operationDateTo: formValue.operationDateTo ? formValue.operationDateTo.split('T')[0] : '',
      remarks: formValue.remarks,
    };

    // Remove campos vazios
    Object.keys(filter).forEach(key => {
      const value = filter[key as keyof NdfOperationFilterRequest];
      if (
        value === '' ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0 && key !== 'operationStrategyTypes') // Não remove operationStrategyTypes mesmo se vazio
      ) {
        delete filter[key as keyof NdfOperationFilterRequest];
      }
    });

    // Atualizar URL com filtros
    this.updateUrlWithFilters(filter);

    this.sendFilter.emit(filter);
    this.sendCloseFilter.emit(false);
    this.emitActiveFilters();
  }

  // Métodos para compatibilidade
  closeFilter(): void {
    this.sendCloseFilter.emit(false);
  }

  resetFilter(): void {
    this.clear();
  }

  filter(): void {
    this.submit();
  }

  // Métodos para calendários
  setCalendarDueAtFrom(dates: string[]): void {
    this.filterForm.patchValue({
      dueAtFrom: dates[0] || '',
    });
  }

  setCalendarDueAtTo(dates: string[]): void {
    this.filterForm.patchValue({
      dueAtTo: dates[0] || '',
    });
  }

  setCalendarOperationDateFrom(dates: string[]): void {
    this.filterForm.patchValue({
      operationDateFrom: dates[0] || '',
    });
  }

  setCalendarOperationDateTo(dates: string[]): void {
    this.filterForm.patchValue({
      operationDateTo: dates[0] || '',
    });
  }

  // Métodos para selects
  handleOptionChange(selectedOption: Option | Option[] | null, key: string): void {
    switch (key) {
      case 'selectedCurrency':
        this.filterForm.patchValue({
          internationalCurrencyId: (selectedOption as Option)?.value || '',
        });
        break;
      case 'selectedFinancialInstitution':
        this.filterForm.patchValue({
          financialInstitutionId: (selectedOption as Option)?.value || '',
          financialInstitutionName: selectedOption ? (selectedOption as Option).label : '',
        });
        break;
      case 'operationStatus':
        this.filterForm.patchValue({
          operationStatus: (selectedOption as Option)?.value || '',
        });
        break;
      case 'financialStatus':
        this.filterForm.patchValue({
          financialStatus: (selectedOption as Option)?.value || '',
        });
        break;
      case 'composition':
        this.filterForm.patchValue({
          composition: (selectedOption as Option)?.value || '',
        });
        break;
      case 'withoutInstallments':
        this.filterForm.patchValue({
          withoutInstallments: (selectedOption as Option)?.value || '',
        });
        break;
      case 'operationStrategyTypes':
        this.filterForm.patchValue({
          operationStrategyTypes: Array.isArray(selectedOption)
            ? selectedOption.map(opt => opt.value)
            : [],
        });
        break;
    }
  }

  // Getters para o template
  get dueAtFrom(): string {
    return this.filterForm?.get('dueAtFrom')?.value || '';
  }

  get dueAtTo(): string {
    return this.filterForm?.get('dueAtTo')?.value || '';
  }

  get operationDateFrom(): string {
    return this.filterForm?.get('operationDateFrom')?.value || '';
  }

  get operationDateTo(): string {
    return this.filterForm?.get('operationDateTo')?.value || '';
  }

  get selectedCurrency(): Option | null {
    const currencyId = this.filterForm?.get('internationalCurrencyId')?.value;
    return this.currenciesCache.find(c => c.value === currencyId) || null;
  }

  get selectedFinancialInstitution(): Option | null {
    const institutionId = this.filterForm?.get('financialInstitutionId')?.value;
    return this.financialInstitutionsCache.find(f => f.value === institutionId) || null;
  }

  get selectedOperationStatus(): Option | null {
    const status = this.filterForm?.get('operationStatus')?.value;
    return this.operationStatusOptions.find(o => o.value === status) || null;
  }

  get selectedFinancialStatus(): Option | null {
    const status = this.filterForm?.get('financialStatus')?.value;
    return this.financialStatusOptions.find(f => f.value === status) || null;
  }

  get selectedComposition(): Option | null {
    const composition = this.filterForm?.get('composition')?.value;
    return this.compositionOptions.find(c => c.value === composition) || null;
  }

  get selectedWithoutInstallments(): Option | null {
    const value = this.filterForm?.get('withoutInstallments')?.value;
    return this.installmentsOptions.find(c => c.value === value) || null;
  }

  get selectedOperationStrategyTypes(): Option[] {
    const types = this.filterForm?.get('operationStrategyTypes')?.value || [];
    return types
      .map((type: string) => this.operationStrategyTypeOptions.find(opt => opt.value === type))
      .filter((opt: Option | undefined): opt is Option => opt !== undefined);
  }

  /**
   * Gera e emite as configurações dos filtros ativos
   */
  private emitActiveFilters(): void {
    const activeFilterConfigs: ActiveFilterConfig[] = [];

    if (this.currentFilters) {
      // Moeda
      if (this.currentFilters.internationalCurrencyId) {
        const currencyLabel = this.getCurrencyLabelById(
          this.currentFilters.internationalCurrencyId
        );
        activeFilterConfigs.push({
          propertyName: 'internationalCurrencyId',
          label: 'internationalCurrencyId',
          type: 'text',
          value: currencyLabel,
          group: this.t(this.tk.Management.NdfCurrency),
        });
      }

      // Instituição Financeira
      if (
        this.currentFilters.financialInstitutionId ||
        this.currentFilters.financialInstitutionName
      ) {
        const institutionLabel =
          this.currentFilters.financialInstitutionName ||
          this.getFinancialInstitutionLabelById(this.currentFilters.financialInstitutionId || '');

        activeFilterConfigs.push({
          propertyName: 'financialInstitutionId',
          label: 'financialInstitutionId',
          type: 'text',
          value: institutionLabel,
          group: this.t(this.tk.Management.NdfBank),
        });
      }

      // Status da Operação
      if (this.currentFilters.operationStatus) {
        const statusLabel = this.getOperationStatusLabel(this.currentFilters.operationStatus);
        activeFilterConfigs.push({
          propertyName: 'operationStatus',
          label: 'operationStatus',
          type: 'text',
          value: statusLabel,
          group: this.t(this.tk.Management.NdfOperationStatus),
        });
      }

      // Modalidades
      if (this.currentFilters.operationStrategyTypes?.length) {
        const modalidadesLabels = this.currentFilters.operationStrategyTypes
          .map(type => this.getOperationStrategyTypeLabel(type))
          .join(', ');

        activeFilterConfigs.push({
          propertyName: 'operationStrategyTypes',
          label: 'operationStrategyTypes',
          type: 'text',
          value: modalidadesLabels,
          group: 'Modalidades',
        });
      }

      // Status Financeiro
      if (this.currentFilters.financialStatus) {
        const statusLabel = this.getFinancialStatusLabel(this.currentFilters.financialStatus);
        activeFilterConfigs.push({
          propertyName: 'financialStatus',
          label: 'financialStatus',
          type: 'text',
          value: statusLabel,
          group: this.t(this.tk.Management.NdfFinancialStatus),
        });
      }

      // Composição
      if (this.currentFilters.composition) {
        const compositionLabel = this.getCompositionLabel(this.currentFilters.composition);
        activeFilterConfigs.push({
          propertyName: 'composition',
          label: 'composition',
          type: 'text',
          value: compositionLabel,
          group: this.t(this.tk.Management.NdfComposition),
        });
      }

      // Parcelas Vinculadas
      if (this.currentFilters.withoutInstallments !== undefined) {
        const label =
          this.currentFilters.withoutInstallments === true ? 'Sem Parcelas' : 'Com Parcelas';
        activeFilterConfigs.push({
          propertyName: 'withoutInstallments',
          label: 'withoutInstallments',
          type: 'text',
          value: label,
          group: 'Parcelas Vinculadas',
        });
      }

      // Ranges de valores
      if (this.currentFilters.valueMEFrom || this.currentFilters.valueMETo) {
        const valueRange = this.formatRange(
          parseFloat(this.currentFilters.valueMEFrom || '0'),
          parseFloat(this.currentFilters.valueMETo || '0')
        );
        activeFilterConfigs.push({
          propertyName: 'valueMEFrom',
          label: 'valueRange',
          type: 'text',
          value: valueRange,
          group: this.t(this.tk.Management.NdfValue),
        });
      }

      if (this.currentFilters.notionalAmountFrom || this.currentFilters.notionalAmountTo) {
        const notionalRange = this.formatRange(
          parseFloat(this.currentFilters.notionalAmountFrom || '0'),
          parseFloat(this.currentFilters.notionalAmountTo || '0')
        );
        activeFilterConfigs.push({
          propertyName: 'notionalAmountFrom',
          label: 'notionalRange',
          type: 'text',
          value: notionalRange,
          group: this.t(this.tk.Management.NdfNotional),
        });
      }

      if (this.currentFilters.forwardRateFrom || this.currentFilters.forwardRateTo) {
        const forwardRange = this.formatRange(
          parseFloat(this.currentFilters.forwardRateFrom || '0'),
          parseFloat(this.currentFilters.forwardRateTo || '0')
        );
        activeFilterConfigs.push({
          propertyName: 'forwardRateFrom',
          label: 'forwardRange',
          type: 'text',
          value: forwardRange,
          group: this.t(this.tk.Management.NdfForward),
        });
      }

      if (this.currentFilters.capValueFrom || this.currentFilters.capValueTo) {
        const capRange = this.formatRange(
          parseFloat(this.currentFilters.capValueFrom || '0'),
          parseFloat(this.currentFilters.capValueTo || '0')
        );
        activeFilterConfigs.push({
          propertyName: 'capValueFrom',
          label: 'capRange',
          type: 'text',
          value: capRange,
          group: this.t(this.tk.Management.NdfCap),
        });
      }

      if (
        this.currentFilters.referenceExchangeRateFrom ||
        this.currentFilters.referenceExchangeRateTo
      ) {
        const exchangeRange = this.formatRange(
          parseFloat(this.currentFilters.referenceExchangeRateFrom || '0'),
          parseFloat(this.currentFilters.referenceExchangeRateTo || '0')
        );
        activeFilterConfigs.push({
          propertyName: 'referenceExchangeRateFrom',
          label: 'exchangeRange',
          type: 'text',
          value: exchangeRange,
          group: this.t(this.tk.Management.NdfExchange),
        });
      }

      if (this.currentFilters.daysCountFrom || this.currentFilters.daysCountTo) {
        const daysRange = `${this.currentFilters.daysCountFrom || '0'} - ${this.currentFilters.daysCountTo || '0'} dias`;
        activeFilterConfigs.push({
          propertyName: 'daysCountFrom',
          label: 'daysRange',
          type: 'text',
          value: daysRange,
          group: this.t(this.tk.Management.NdfCalendarDays),
        });
      }

      // Datas
      if (this.currentFilters.dueAtFrom) {
        activeFilterConfigs.push({
          propertyName: 'dueAtFrom',
          label: 'dueAtFrom',
          type: 'date',
          value: this.currentFilters.dueAtFrom,
          group: this.t(this.tk.Management.NdfDueDateInitial),
        });
      }

      if (this.currentFilters.dueAtTo) {
        activeFilterConfigs.push({
          propertyName: 'dueAtTo',
          label: 'dueAtTo',
          type: 'date',
          value: this.currentFilters.dueAtTo,
          group: this.t(this.tk.Management.NdfDueDateFinal),
        });
      }

      if (this.currentFilters.operationDateFrom) {
        activeFilterConfigs.push({
          propertyName: 'operationDateFrom',
          label: 'operationDateFrom',
          type: 'date',
          value: this.currentFilters.operationDateFrom,
          group: this.t(this.tk.Management.OperationStartDate),
        });
      }

      if (this.currentFilters.operationDateTo) {
        activeFilterConfigs.push({
          propertyName: 'operationDateTo',
          label: 'operationDateTo',
          type: 'date',
          value: this.currentFilters.operationDateTo,
          group: this.t(this.tk.Management.OperationEndDate),
        });
      }

      // Referência
      if (this.currentFilters.remarks) {
        activeFilterConfigs.push({
          propertyName: 'remarks',
          label: 'remarks',
          type: 'text',
          value: this.currentFilters.remarks,
          group: this.t(this.tk.Management.NdfReference),
        });
      }
    }

    this.sendActiveFilters.emit(activeFilterConfigs);
  }

  /**
   * Métodos para obter labels pelos IDs usando os dados em cache
   */
  private getCurrencyLabelById(id: string): string {
    const currency = this.currenciesCache.find(c => c.value === id);
    return currency?.label || id;
  }

  private getFinancialInstitutionLabelById(id: string): string {
    const institution = this.financialInstitutionsCache.find(i => i.value === id);
    return institution?.label || id;
  }

  private getOperationStatusLabel(value: string): string {
    const option = this.operationStatusOptions.find(o => o.value === value);
    return option ? option.label : value;
  }

  private getFinancialStatusLabel(value: string): string {
    const option = this.financialStatusOptions.find(o => o.value === value);
    return option ? option.label : value;
  }

  private getCompositionLabel(value: string): string {
    const option = this.compositionOptions.find(o => o.value === value);
    return option ? option.label : value;
  }

  private getOperationStrategyTypeLabel(value: string): string {
    const option = this.operationStrategyTypeOptions.find(o => o.value === value);
    return option ? option.label : value;
  }

  /**
   * Formatar range de valores monetários
   */
  private formatRange(min?: number, max?: number): string {
    const minValue = min && min > 0 ? min : null;
    const maxValue = max && max > 0 ? max : null;

    if (minValue && maxValue && minValue !== maxValue) {
      return `${minValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} - ${maxValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    } else if (minValue) {
      return `A partir de ${minValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    } else if (maxValue) {
      return `Até ${maxValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    }
    return '';
  }
}
